import React from 'react';
// Lightweight markdown renderer (headings, bold, inline code, code blocks, lists)
export function Markdown({
  content
}: {
  content: string;
}) {
  const blocks: React.ReactNode[] = [];
  const lines = content.split('\n');
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    // code block
    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3);
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push(<pre key={key++} className="my-2 overflow-x-auto rounded-lg border border-slate-700 bg-slate-950 p-3" data-id="element-175">
          {lang && <div className="mb-1 text-xs text-slate-500" data-id="element-176">{lang}</div>}
          <code className="font-mono text-xs text-slate-200" data-id="element-177">
            {buf.join('\n')}
          </code>
        </pre>);
      continue;
    }
    // headings
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const sizes = ['text-lg', 'text-base', 'text-sm', 'text-sm'];
      blocks.push(<p key={key++} className={`mt-3 mb-1 font-semibold text-slate-100 ${sizes[level - 1]}`} data-id="element-178">
          {renderInline(h[2])}
        </p>);
      i++;
      continue;
    }
    // list group
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(<ul key={key++} className="my-1.5 list-disc space-y-0.5 pl-5 text-sm text-slate-300" data-id="element-179">
          {items.map((it, idx) => <li key={idx} data-id="element-180">{renderInline(it)}</li>)}
        </ul>);
      continue;
    }
    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(<ol key={key++} className="my-1.5 list-decimal space-y-0.5 pl-5 text-sm text-slate-300" data-id="element-181">
          {items.map((it, idx) => <li key={idx} data-id="element-182">{renderInline(it)}</li>)}
        </ol>);
      continue;
    }
    if (line.trim() === '') {
      i++;
      continue;
    }
    blocks.push(<p key={key++} className="my-1 text-sm leading-relaxed text-slate-300" data-id="element-183">
        {renderInline(line)}
      </p>);
    i++;
  }
  return <div className="break-words" data-id="element-184">{blocks}</div>;
}
function renderInline(text: string): React.ReactNode {
  // split by inline code and bold
  const parts: React.ReactNode[] = [];
  const regex = /(`[^`]+`|\*\*[^*]+\*\*|_[^_]+_)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const token = m[0];
    if (token.startsWith('`')) {
      parts.push(<code key={k++} className="rounded bg-slate-800 px-1 py-0.5 font-mono text-xs text-indigo-300" data-id="element-185">
          {token.slice(1, -1)}
        </code>);
    } else if (token.startsWith('**')) {
      parts.push(<strong key={k++} className="font-semibold text-slate-100" data-id="element-186">
          {token.slice(2, -2)}
        </strong>);
    } else if (token.startsWith('_')) {
      parts.push(<em key={k++} className="text-slate-400" data-id="element-187">
          {token.slice(1, -1)}
        </em>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}